import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApprovalQueue } from '@/components/staffAttendance/ApprovalQueue';
import { DailySection } from '@/components/staffAttendance/DailySection';
import { HeadcountBoard } from '@/components/staffAttendance/HeadcountBoard';
import { ManpowerBoard } from '@/components/staffAttendance/ManpowerBoard';
import { MandayReport } from '@/components/staffAttendance/MandayReport';
import { ContractPayroll } from '@/components/staffAttendance/ContractPayroll';
import { RegularPayroll } from '@/components/staffAttendance/RegularPayroll';
import { MissedCheckouts } from '@/components/staffAttendance/MissedCheckouts';
import { EsslReconcile } from '@/components/staffAttendance/EsslReconcile';
import { MonthlyView } from '@/components/staffAttendance/MonthlyView';
import { ReferencePhotos } from '@/components/staffAttendance/ReferencePhotos';
import { SectionDevices } from '@/components/staffAttendance/SectionDevices';
import { AttendanceSettings } from '@/components/staffAttendance/AttendanceSettings';
import { GuideMenu } from '@/components/staffAttendance/GuideMenu';
import { StaffSearchBar } from '@/components/staffAttendance/StaffSearch';
import { HrLookup } from '@/components/myPay/HrLookup';
import { staffAttApi } from '@/services/staffAttendanceApi';

const SEARCH_TABS = new Set(['approvals', 'daily', 'headcount', 'manpower', 'monthly', 'references', 'missed', 'essl', 'payroll', 'regpayroll']);

const NoAccess = () => (
  <div className="text-center py-16 text-gray-500 space-y-2" data-testid="hub-not-reviewer">
    <ShieldAlert className="w-10 h-10 mx-auto text-amber-500" />
    <p>You don't have Staff Attendance approval or report access. Ask Admin to tick it in <b>User Management</b>.</p>
  </div>
);

export default function StaffAttendanceHub() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [perm, setPerm] = useState(null);
  const [pending, setPending] = useState(null);

  const refreshCount = useCallback(() => staffAttApi.queueCount().then((d) => setPending(d)).catch(() => {}), []);
  useEffect(() => {
    staffAttApi.sections().then(setMeta).catch(() => setMeta({ sections: [], supervisors: [] }));
    staffAttApi.settings().then(setPerm).catch(() => setPerm({}));
    refreshCount();
  }, [refreshCount]);

  const sections = meta?.sections || [];
  const approve = !!perm?.can_approve;
  const view = !!perm?.can_view;
  const payroll = !!perm?.can_payroll;
  const allowed = new Set([approve && 'approvals', view && 'daily', view && 'headcount', (approve || view) && 'manpower', view && 'manday', view && 'monthly',
    approve && 'references', approve && 'devices', perm?.can_edit && 'missed', perm?.can_edit && 'essl', payroll && 'regpayroll', payroll && 'payroll',
    payroll && 'paylookup', perm?.can_edit && 'settings'].filter(Boolean));
  const fallback = approve ? 'approvals' : view ? 'daily' : 'payroll';
  const tab = allowed.has(params.get('tab')) ? params.get('tab') : fallback;
  const q = params.get('q') || '';
  const nav = (t, s) => setParams({ tab: t, ...(s ? { q: s } : {}) }, { replace: true });

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="staff-attendance-hub">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-xl font-bold">Staff Attendance</h1>
          <div className="ml-auto flex items-center gap-2">
            {perm && <GuideMenu which={[perm.can_edit && 'hr', perm.can_approve && 'supervisor', 'staff'].filter(Boolean)} />}
            {perm?.can_punch_own && <Button variant="outline" onClick={() => navigate('/attendance/my')} className="bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="hub-my-attendance">My Attendance</Button>}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {!perm && <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />}
        {perm && !approve && !view && !payroll && <NoAccess />}
        {perm && (approve || view || payroll) && (
          <Tabs value={tab} onValueChange={(v) => nav(v, q)} className="space-y-4">
            <TabsList className="flex-wrap h-auto">
              {approve && (
                <TabsTrigger value="approvals" data-testid="hub-tab-approvals">
                  Approvals{pending?.pending ? <span className="ml-2 rounded-full bg-amber-500 text-white px-2 text-xs" data-testid="hub-pending-count">{pending.pending}</span> : null}
                </TabsTrigger>
              )}
              {view && <TabsTrigger value="daily" data-testid="hub-tab-daily">Daily</TabsTrigger>}
              {view && <TabsTrigger value="headcount" data-testid="hub-tab-headcount">Headcount</TabsTrigger>}
              {(approve || view) && <TabsTrigger value="manpower" data-testid="hub-tab-manpower">Manpower</TabsTrigger>}
              {view && <TabsTrigger value="manday" data-testid="hub-tab-manday">Manday Report</TabsTrigger>}
              {view && <TabsTrigger value="monthly" data-testid="hub-tab-monthly">Monthly</TabsTrigger>}
              {approve && <TabsTrigger value="references" data-testid="hub-tab-references">Reference Photos</TabsTrigger>}
              {approve && <TabsTrigger value="devices" data-testid="hub-tab-devices">Section Devices</TabsTrigger>}
              {perm.can_edit && <TabsTrigger value="missed" data-testid="hub-tab-missed">Missed Check-outs</TabsTrigger>}
              {perm.can_edit && <TabsTrigger value="essl" data-testid="hub-tab-essl">eSSL Reconcile</TabsTrigger>}
              {payroll && <TabsTrigger value="regpayroll" data-testid="hub-tab-regpayroll">Regular Payroll</TabsTrigger>}
              {payroll && <TabsTrigger value="payroll" data-testid="hub-tab-payroll">Contract Payroll</TabsTrigger>}
              {payroll && <TabsTrigger value="paylookup" data-testid="hub-tab-paylookup">Staff Pay Lookup</TabsTrigger>}
              {perm.can_edit && <TabsTrigger value="settings" data-testid="hub-tab-settings">Settings</TabsTrigger>}
            </TabsList>
            {SEARCH_TABS.has(tab) && <StaffSearchBar value={q} onChange={(v) => nav(tab, v)} />}
            {approve && <TabsContent value="approvals"><ApprovalQueue sections={sections} onChanged={refreshCount} search={q} /></TabsContent>}
            {view && <TabsContent value="daily"><DailySection sections={sections} search={q} /></TabsContent>}
            {view && <TabsContent value="headcount"><HeadcountBoard search={q} /></TabsContent>}
            {(approve || view) && <TabsContent value="manpower"><ManpowerBoard search={q} /></TabsContent>}
            {view && <TabsContent value="manday"><MandayReport /></TabsContent>}
            {view && <TabsContent value="monthly"><MonthlyView sections={sections} search={q} /></TabsContent>}
            {approve && <TabsContent value="references"><ReferencePhotos sections={sections} search={q} /></TabsContent>}
            {approve && <TabsContent value="devices"><SectionDevices sections={sections} /></TabsContent>}
            {perm.can_edit && <TabsContent value="missed"><MissedCheckouts sections={sections} search={q} /></TabsContent>}
            {perm.can_edit && <TabsContent value="essl"><EsslReconcile sections={sections} search={q} /></TabsContent>}
            {payroll && <TabsContent value="payroll"><ContractPayroll sections={meta?.payroll_sections || sections} search={q} canEdit={!!perm.can_edit} /></TabsContent>}
            {payroll && <TabsContent value="regpayroll"><RegularPayroll sections={meta?.payroll_sections || sections} search={q} canEdit={!!perm.can_edit} /></TabsContent>}
            {payroll && <TabsContent value="paylookup"><HrLookup /></TabsContent>}
            {perm.can_edit && <TabsContent value="settings"><AttendanceSettings sections={sections} supervisors={meta?.supervisors || []} /></TabsContent>}
          </Tabs>
        )}
      </main>
    </div>
  );
}
